/**
 * Returning leads, and asking to.
 *
 * - Staff, one lead: pick which buyers' sales to return (a multi-sold lead
 *   can be returned by one buyer while the others' sales stand), a reason
 *   and a note. A sale outside its buyer's return window needs an override
 *   reason.
 * - Staff, many leads (the Leads list): a reason, a note, optionally one
 *   buyer; every buyer each lead is still sold to otherwise.
 * - A buyer login, one lead: ask for a return, inside the window. Staff
 *   approve it, unless the organisation approves them automatically.
 *
 * Nothing is sent until the button is pressed: cancelling cancels.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Checkbox, Group, Loader, Modal, Select, Stack, Text, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useCreateReturns, useLeadReturns, useRequestReturn, useReturnsSettings, type ReturnableSale,
} from '../../api/returns';
import { useEntities } from '../../api/queries';
import { formatGBP } from '../../theme';
import { useVocab } from '../../vocab';

export type ReturnTarget =
  | { kind: 'lead'; leadId: string }
  | { kind: 'bulk'; leadIds: string[] };

/** "Open until 25 Oct", "Closed on 12 Oct", "No returns". */
export function windowWords(s: Pick<ReturnableSale, 'window_open' | 'window_closes_at' | 'window_days'>): string {
  if (s.window_days <= 0) return 'No returns';
  const at = s.window_closes_at
    ? new Date(s.window_closes_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' })
    : '';
  return s.window_open ? `Window open until ${at}` : `Window closed on ${at}`;
}

export function ReturnDialog({
  target, opened, onClose, asBuyer = false, onDone,
}: {
  target: ReturnTarget | null;
  opened: boolean;
  onClose: () => void;
  /** A buyer login asking, rather than staff returning. */
  asBuyer?: boolean;
  onDone?: () => void;
}) {
  const v = useVocab();
  const settings = useReturnsSettings(opened);
  const leadId = target?.kind === 'lead' ? target.leadId : null;
  const lead = useLeadReturns(leadId, opened);
  const buyers = useEntities('buyers', opened && target?.kind === 'bulk' && !asBuyer);
  const create = useCreateReturns();
  const request = useRequestReturn();

  const [picked, setPicked] = useState<string[]>([]);
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [override, setOverride] = useState('');
  const [bulkBuyer, setBulkBuyer] = useState<string | null>(null);
  const [failures, setFailures] = useState<{ id: string; error: string }[]>([]);

  const open = useMemo(
    () => (lead.data?.sales ?? []).filter((s) => !s.returned && !(asBuyer && s.return_id)),
    [lead.data, asBuyer],
  );

  // Start with every returnable sale ticked, as the whole-lead return did.
  useEffect(() => {
    if (!opened) return;
    setPicked(open.filter((s) => !asBuyer || s.window_open).map((s) => s.buyer_id));
  }, [opened, open, asBuyer]);

  useEffect(() => {
    if (!opened) {
      setReason(null);
      setNote('');
      setOverride('');
      setBulkBuyer(null);
      setFailures([]);
    }
  }, [opened]);

  const reasons = (settings.data?.reasons ?? []).filter((r) => r.active !== false).map((r) => ({ value: r.code, label: r.label }));
  const chosen = open.filter((s) => picked.includes(s.buyer_id));
  const outside = chosen.some((s) => !s.window_open);
  const credit = chosen.reduce((sum, s) => sum + s.price, 0);
  const busy = create.isPending || request.isPending;

  const blocked =
    !reason
    || (target?.kind === 'lead' && chosen.length === 0)
    || (!asBuyer && target?.kind === 'lead' && outside && !override.trim());

  async function submit() {
    if (!target || !reason) return;
    setFailures([]);
    try {
      if (asBuyer && target.kind === 'lead') {
        const r = await request.mutateAsync({ lead_id: target.leadId, reason_code: reason, note: note.trim() || null });
        const approved = r.returns.some((x) => x.status === 'approved');
        notifications.show({
          color: 'teal',
          title: approved ? 'Return approved' : 'Return requested',
          message: approved
            ? `The ${v.one('lead')} has been credited.`
            : `We will look at it and let you know. You can follow it on Returns.`,
        });
        onDone?.();
        onClose();
        return;
      }
      const r = await create.mutateAsync({
        lead_ids: target.kind === 'lead' ? [target.leadId] : target.leadIds,
        buyer_ids: target.kind === 'lead' ? picked : bulkBuyer ? [bulkBuyer] : null,
        reason_code: reason,
        note: note.trim() || null,
        override_reason: override.trim() || null,
      });
      if (r.failed.length > 0) setFailures(r.failed);
      if (r.returns.length > 0) {
        const total = r.returns.reduce((sum, x) => sum + x.credit_amount, 0);
        notifications.show({
          color: 'teal',
          title: r.returns.length === 1 ? 'Returned' : `${r.returns.length} sales returned`,
          message: `${formatGBP(total)} credited. Revenue is now what still stands.`,
        });
      }
      if (r.failed.length === 0) {
        onDone?.();
        onClose();
      }
    } catch (e) {
      setFailures([{ id: '', error: (e as Error).message }]);
    }
  }

  const title = asBuyer
    ? 'Request a return'
    : target?.kind === 'bulk'
      ? `Return ${v.count(target.leadIds.length, 'lead')}`
      : `Return this ${v.one('lead')}`;

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" zIndex={1002}>
      <Stack gap="md">
        {target?.kind === 'lead' && lead.isPending && <Group justify="center" p="md"><Loader size="sm" /></Group>}
        {lead.error && <Alert color="red" variant="light">{lead.error.message}</Alert>}

        {target?.kind === 'lead' && lead.data && open.length === 0 && (
          <Alert color="gray" variant="light">
            {asBuyer
              ? `There is nothing to ask for: this ${v.one('lead')} has been returned, or a return is already waiting.`
              : `Every sale of this ${v.one('lead')} has already been returned.`}
          </Alert>
        )}

        {target?.kind === 'lead' && open.length > 0 && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>
              {asBuyer ? `Your purchase` : open.length > 1 ? `Which ${v.many('buyer')}' sales` : 'The sale'}
            </Text>
            {open.map((s) => (
              <Checkbox
                key={s.buyer_id}
                checked={picked.includes(s.buyer_id)}
                disabled={asBuyer}
                onChange={(e) =>
                  setPicked((p) => (e.currentTarget.checked ? [...p, s.buyer_id] : p.filter((x) => x !== s.buyer_id)))
                }
                label={
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={500}>{s.buyer_name}</Text>
                    <Text size="sm" ff="monospace">{formatGBP(s.price)}</Text>
                    <Badge size="xs" variant="light" color={s.window_open ? 'teal' : 'orange'}>
                      {windowWords(s)}
                    </Badge>
                    {s.return_status === 'requested' && (
                      <Badge size="xs" variant="light" color="blue">Return requested</Badge>
                    )}
                  </Group>
                }
              />
            ))}
            {asBuyer && open.some((s) => !s.window_open) && (
              <Text size="xs" c="dimmed">The return window for this sale has closed.</Text>
            )}
          </Stack>
        )}

        {target?.kind === 'bulk' && !asBuyer && (
          <Select
            label={v.One('buyer')}
            description={`Leave empty to return every sale each ${v.one('lead')} still has.`}
            placeholder={`Every ${v.one('buyer')}`}
            clearable
            searchable
            data={(buyers.data?.entities ?? []).map((b) => ({ value: b.id, label: b.name }))}
            value={bulkBuyer}
            onChange={setBulkBuyer}
            comboboxProps={{ zIndex: 1003 }}
          />
        )}

        <Select
          label="Reason"
          required
          placeholder="Choose a reason"
          data={reasons}
          value={reason}
          onChange={setReason}
          comboboxProps={{ zIndex: 1003 }}
        />
        <Textarea
          label="Note"
          description={asBuyer ? 'Anything that helps us check it.' : 'Kept with the return.'}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />

        {!asBuyer && (target?.kind === 'bulk' || outside) && (
          <Textarea
            label="Override reason"
            required={target?.kind === 'lead'}
            description={
              target?.kind === 'bulk'
                ? `Only used for sales outside their ${v.one('buyer')}'s return window. Without it those are skipped.`
                : `A chosen sale is outside its ${v.one('buyer')}'s return window. Say why it is being returned anyway.`
            }
            value={override}
            onChange={(e) => setOverride(e.currentTarget.value)}
            autosize
            minRows={2}
          />
        )}

        {!asBuyer && (target?.kind === 'bulk' || chosen.length > 0) && (
          <Alert color="red" variant="light" title="This reverses money">
            <Text size="sm">
              {target?.kind === 'lead'
                ? `${formatGBP(credit)} will be credited and taken off this ${v.one('lead')}'s revenue. ${
                  chosen.length < open.length ? 'The other sales stand. ' : ''
                }Caps those sales used this period are given back, and the ${v.one('supplier')} is charged back unless switched off.`
                : `Each sale returned is credited and taken off its ${v.one('lead')}'s revenue, caps are given back, and ${v.many('supplier')} are charged back unless switched off.`}
            </Text>
          </Alert>
        )}

        {failures.length > 0 && (
          <Alert color="orange" variant="light" title={failures.length === 1 && !failures[0]!.id ? 'Not returned' : 'Some were not returned'}>
            <Stack gap={2}>
              {failures.slice(0, 8).map((f, i) => (
                <Text key={`${f.id}-${i}`} size="sm">{f.id ? `${f.id.slice(0, 8)}: ${f.error}` : f.error}</Text>
              ))}
              {failures.length > 8 && <Text size="sm">{`and ${failures.length - 8} more.`}</Text>}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            color={asBuyer ? undefined : 'red'}
            loading={busy}
            disabled={blocked}
            onClick={submit}
          >
            {asBuyer ? 'Request return' : chosen.length > 1 || target?.kind === 'bulk' ? 'Return sales' : 'Return sale'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
